import React, { useEffect, useRef, useState } from 'react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { MapContainer, TileLayer, Polygon, Polyline, Marker, Tooltip, useMap } from 'react-leaflet';
import { renderToStaticMarkup } from 'react-dom/server';
import { Maximize2, Minimize2, LocateFixed, Lightbulb, AlertTriangle, Trash2, TreePine, Droplet, Flag } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { IconTooltip } from '../IconTooltip';
import { SLA_COLORS } from './RequestPinPopup';
import { DateRangePicker, type DateRange } from './DateRangePicker';
import type { PinIcon, RequestPin, SlaState } from '../../utils/requestPins';
import { useCity } from '../../contexts/CityContext';
import { mapViewFor, type Coord, type Sector } from '../../data/communications';
import { districtCenter, districtOutline, sectorLetter, type CityDistrict } from '../../utils/districts';
interface DistrictMapProps {
  districts: CityDistrict[];
  /** Districts drawn with a strong outline and a dark badge. */
  highlightedDistrictIds: string[];
  /** Sectors drawn dashed on top of the districts. */
  overlaySectors: Sector[];
  /** Overlay sectors currently included in the analysis (filled darker). */
  activeSectorIds: string[];
  /** Points the map flies to when the selection changes. */
  focusPoints: Coord[];
  onDistrictClick: (districtId: string) => void;
  /** When set, overlay sectors become clickable. */
  onSectorClick?: (sectorId: string) => void;
  /** Map view: districts (with their sectors) or sectors only. */
  view: 'district' | 'sector';
  /** Shown only when the city has districts. */
  onViewChange?: (view: 'district' | 'sector') => void;
  expanded: boolean;
  onToggleExpanded: () => void;
  showDetails: boolean;
  onToggleDetails: () => void;
  /** Request pins shown when details are on. */
  pins: RequestPin[];
  selectedPinId: string | null;
  onPinClick: (id: string) => void;
  dateRange: DateRange;
  onDateRangeChange: (r: DateRange) => void;
  /** Only show pins in this SLA state (null = all). */
  slaFilter: SlaState | null;
  onSlaFilterChange: (s: SlaState | null) => void;
  /**
   * Restricted view: the area the "home" button returns to (instead of the
   * whole city). The map also opens fitted on it.
   */
  homePoints?: Coord[];
  homeLabel?: string;
  /** Districts "à surveiller" (on-time rate under 80 %), filled amber. */
  watchDistrictIds?: string[];
  /** Sectors filled amber in the sector view. */
  watchSectorIds?: string[];
  /** Called by the home button, e.g. to select the whole city. */
  onHome?: () => void;
}
const GREEN = {
  fill: '#10B981',
  line: '#059669',
  strong: '#065F46',
  sector: '#047857'
};
const AMBER = {
  fill: '#F59E0B',
  line: '#D97706',
  strong: '#92400E',
  sector: '#B45309'
};
const PIN_ICONS: Record<PinIcon, typeof Lightbulb> = {
  light: Lightbulb,
  road: AlertTriangle,
  bin: Trash2,
  tree: TreePine,
  water: Droplet,
  sign: Flag
};
const pinIconCache = new Map<string, L.DivIcon>();
function pinIcon(kind: PinIcon, state: SlaState, selected = false) {
  const key = `${kind}-${state}-${selected ? 's' : 'n'}`;
  const cached = pinIconCache.get(key);
  if (cached) return cached;
  const Icon = PIN_ICONS[kind];
  const svg = renderToStaticMarkup(<Icon size={14} color="#FFFFFF" strokeWidth={2.25} data-id="element-5392" data-mp="24r04ya9vzl40" />);
  const color = SLA_COLORS[state];
  const icon = L.divIcon({
    className: '',
    iconSize: [28, 34],
    iconAnchor: [14, 34],
    popupAnchor: [0, -30],
    html: `<div style="position:relative;width:28px;height:34px;cursor:pointer"><div style="width:28px;height:28px;border-radius:9999px;background:${color};border:2px solid #fff;box-shadow:${selected ? '0 0 0 3px #0F172A,' : ''}0 1px 4px rgba(15,23,42,.35);display:flex;align-items:center;justify-content:center">${svg}</div><div style="position:absolute;left:10px;top:24px;width:0;height:0;border-left:4px solid transparent;border-right:4px solid transparent;border-top:8px solid ${color}"></div></div>`
  });
  pinIconCache.set(key, icon);
  return icon;
}

/**
 * When the map is enlarged or shrunk, wait for the size transition to end,
 * then re-fit the view so the zoom follows the new size.
 */
function MapResize({
  trigger,
  points,
  center,
  zoom
}: {
  trigger: boolean;
  points: Coord[];
  center: Coord;
  zoom: number;
}) {
  const map = useMap();
  const mounted = useRef(false);
  const latest = useRef({
    points,
    center,
    zoom
  });
  latest.current = {
    points,
    center,
    zoom
  };

  // Keep tiles correct during the transition.
  useEffect(() => {
    const el = map.getContainer();
    const ro = new ResizeObserver(() => map.invalidateSize({
      pan: false
    }));
    ro.observe(el);
    return () => ro.disconnect();
  }, [map]);
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    const id = window.setTimeout(() => {
      map.invalidateSize({
        pan: false
      });
      const {
        points: p,
        center: c,
        zoom: z
      } = latest.current;
      if (p.length > 0) {
        map.flyToBounds(L.latLngBounds(p), {
          padding: [40, 40],
          maxZoom: 15,
          duration: 0.5
        });
      } else {
        map.flyTo(c, trigger ? z + 1 : z, {
          duration: 0.5
        });
      }
    }, 340);
    return () => window.clearTimeout(id);
  }, [trigger, map]);
  return null;
}

/** Flies to the focus (after the first render) or back to the whole city. */
function MapFocus({
  points,
  overviewKey,
  center,
  zoom,
  homePoints,
  fitOnMount
}: {
  points: Coord[];
  overviewKey: number;
  center: Coord;
  zoom: number;
  homePoints?: Coord[];
  fitOnMount?: boolean;
}) {
  const map = useMap();
  const mounted = useRef(false);
  const key = points.map(p => p.join(',')).join(';');
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      if (fitOnMount && points.length > 0) {
        map.fitBounds(L.latLngBounds(points), {
          padding: [40, 40],
          maxZoom: 15
        });
      }
      return;
    }
    if (points.length > 0) {
      map.flyToBounds(L.latLngBounds(points), {
        padding: [40, 40],
        maxZoom: 15,
        duration: 0.6
      });
    }
  }, [key, map]);
  useEffect(() => {
    if (overviewKey <= 0) return;
    if (homePoints && homePoints.length > 0) {
      map.flyToBounds(L.latLngBounds(homePoints), {
        padding: [40, 40],
        maxZoom: 15,
        duration: 0.6
      });
    } else {
      map.flyTo(center, zoom, {
        duration: 0.6
      });
    }
  }, [overviewKey, map]);
  return null;
}
function badgeIcon(text: string, selected: boolean) {
  const bg = selected ? '#065F46' : '#FFFFFF';
  const fg = selected ? '#FFFFFF' : '#065F46';
  return L.divIcon({
    className: '',
    iconSize: [0, 0],
    iconAnchor: [0, 0],
    html: `<div style="transform:translate(-50%,-50%);min-width:26px;height:26px;padding:0 7px;border-radius:9999px;background:${bg};color:${fg};border:2px solid #059669;display:flex;align-items:center;justify-content:center;font:700 12px/1 system-ui,sans-serif;white-space:nowrap;box-shadow:0 1px 3px rgba(15,23,42,.25);cursor:pointer">${text}</div>`
  });
}
export function DistrictMap({
  districts,
  highlightedDistrictIds,
  overlaySectors,
  activeSectorIds,
  focusPoints,
  onDistrictClick,
  onSectorClick,
  view: mapMode,
  onViewChange,
  expanded,
  onToggleExpanded,
  showDetails,
  onToggleDetails,
  pins,
  selectedPinId,
  onPinClick,
  dateRange,
  onDateRangeChange,
  slaFilter,
  onSlaFilterChange,
  homePoints,
  homeLabel: homeLabelProp,
  watchDistrictIds = [],
  watchSectorIds = [],
  onHome
}: DistrictMapProps) {
  const {
    t
  } = useLanguage();
  const homeLabel = homeLabelProp ?? (onHome ? t('Sélectionner toute la ville', 'Select the whole city') : t('Voir toute la ville', 'Show whole city'));
  const {
    city
  } = useCity();
  const view = mapViewFor(city);
  const [overviewKey, setOverviewKey] = useState(0);
  const showDistricts = mapMode === 'district';
  const shownDistricts = showDistricts ? districts : [];
  const isOn = (id: string) => highlightedDistrictIds.includes(id);
  const tone = (id: string) => watchDistrictIds.includes(id) ? AMBER : GREEN;
  return <div className="border border-slate-200 rounded-xl shadow-sm bg-white p-4 flex flex-col gap-3 h-full" data-id="element-5393" data-mp="2h89bevc7fqrk">
      <div className="flex items-center justify-between gap-3 flex-wrap" data-id="element-5394" data-mp="2m5j9t0hkncrj">
        <div className="flex items-center gap-4 flex-wrap" data-id="element-5395" data-mp="2qznh5kur1mt5">
        {onViewChange ? <div className="flex items-center gap-2 font-body" data-id="element-5396" data-mp="2vwdyebiaiog5">
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wide" data-id="element-5397" data-mp="1y9oqxm4tjjmw">
              {t('Par :', 'By:')}
            </span>
            <div role="radiogroup" aria-label={t('Vue de la carte', 'Map view')} className="inline-flex p-0.5 rounded-lg bg-slate-100" data-id="element-5398" data-mp="239jdxelh3p2o">
              {(['district', 'sector'] as const).map(v => <button key={v} type="button" role="radio" aria-checked={mapMode === v} onClick={() => onViewChange(v)} className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${mapMode === v ? 'bg-white text-[#0F172A] shadow-sm' : 'text-slate-500 hover:text-[#0F172A]'}`} data-id="element-5399" data-mp="3jn02jrp6bokv">
                  {v === 'district' ? t('District', 'District') : t('Secteur', 'Sector')}
                </button>)}
            </div>
          </div> : <div className="text-xs font-medium text-slate-500 uppercase tracking-wide font-body" data-id="element-5400" data-mp="t8nqrwtloqb6">
            {t(`Carte des secteurs · ${city}`, `Sector map · ${city}`)}
          </div>}
        <div className="flex items-center gap-2 font-body" data-id="element-5401" data-mp="kc6cikpxwcb7">
          <button type="button" role="switch" aria-checked={showDetails} onClick={onToggleDetails} className="inline-flex items-center gap-2 px-2 py-1 rounded-md text-xs font-medium text-slate-600 hover:bg-slate-50 transition-colors" data-id="element-5402" data-mp="fc0tfsxqjy4q">
            {t('Détails', 'Details')}
            <span className={`relative w-7 h-4 rounded-full transition-colors ${showDetails ? 'bg-[#4F46E5]' : 'bg-slate-300'}`} data-id="element-5403" data-mp="2vhztlyi5xyan">
              <span className={`absolute top-0.5 left-0.5 w-3 h-3 rounded-full bg-white shadow-sm transition-transform ${showDetails ? 'translate-x-3' : ''}`} data-id="element-5404" data-mp="34fsah73j9e05" />
            </span>
          </button>
          {showDetails && (['ok', 'risk', 'late'] as const).map(s => {
            const label = s === 'ok' ? t('SLA respecté', 'SLA met') : s === 'risk' ? t('Proche de l’échéance', 'Near deadline') : t('SLA dépassé', 'SLA missed');
            const count = pins.filter(p => p.state === s).length;
            const active = slaFilter === s;
            const dimmed = slaFilter !== null && !active;
            return <IconTooltip key={s} label={active ? t(`${label} · retirer le filtre`, `${label} · clear filter`) : label} data-id="element-5405" data-mp="1ettes08uiy5x">
                  <button type="button" onClick={() => onSlaFilterChange(active ? null : s)} aria-pressed={active} aria-label={`${label} : ${count}`} className={`min-w-[22px] h-[22px] px-1.5 inline-flex items-center justify-center rounded-full text-[11px] font-semibold text-white tabular-nums transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4F46E5] ${active ? 'ring-2 ring-offset-1 ring-[#0F172A]' : ''} ${dimmed ? 'opacity-35 hover:opacity-70' : 'hover:brightness-110'}`} style={{
                background: SLA_COLORS[s]
              }} data-id="element-5406" data-mp="14z9uuqj7nkne">
                    {count}
                  </button>
                </IconTooltip>;
          })}
        </div>
        </div>
        <div className="flex items-center gap-2 font-body" data-id="element-5407" data-mp="2ev6mnevr6zbp">
          <DateRangePicker value={dateRange} onChange={onDateRangeChange} data-id="element-5408" data-mp="2ext0fw98mb2d" />
          <IconTooltip label={homeLabel} data-id="element-5409" data-mp="2a3f49rmr3zwn">
            <button type="button" onClick={() => {
            setOverviewKey(k => k + 1);
            onHome?.();
          }} aria-label={homeLabel} className="w-7 h-7 inline-flex items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:text-[#4F46E5] hover:border-indigo-200 hover:bg-indigo-50 transition-colors" data-id="element-5410" data-mp="2566jdja7xhrp">
              <LocateFixed className="w-3.5 h-3.5" data-id="element-5411" data-mp="1m7uazzr4sbx6" />
            </button>
          </IconTooltip>
          <IconTooltip label={expanded ? t('Réduire la carte', 'Shrink map') : t('Agrandir la carte', 'Enlarge map')} data-id="element-5412" data-mp="3lkevsl19yapm">
            <button type="button" onClick={onToggleExpanded} aria-label={expanded ? t('Réduire la carte', 'Shrink map') : t('Agrandir la carte', 'Enlarge map')} aria-pressed={expanded} className="w-7 h-7 inline-flex items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:text-[#4F46E5] hover:border-indigo-200 hover:bg-indigo-50 transition-colors" data-id="element-5413" data-mp="3glv9ukmokyx9">
              {expanded ? <Minimize2 className="w-3.5 h-3.5" data-id="element-5414" data-mp="3a4tov0uo50we" /> : <Maximize2 className="w-3.5 h-3.5" data-id="element-5415" data-mp="3a521qsqsk67l" />}
            </button>
          </IconTooltip>
        </div>
      </div>
      <div className={`${expanded ? 'h-[620px]' : 'h-[420px]'} rounded-lg overflow-hidden border border-slate-200 transition-[height] duration-300`} aria-label={t(`Carte des districts de ${city}`, `Map of ${city} districts`)} data-id="element-5416" data-mp="3k2pytpbvdpsx">
        <MapContainer key={city} center={view.center} zoom={view.zoom} scrollWheelZoom wheelPxPerZoomLevel={120} style={{
        height: '100%',
        width: '100%'
      }} data-id="element-5417" data-mp="zb7cjusqbfr6">
          <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-5418" data-mp="syuz9nih56qz" />
          <MapFocus points={focusPoints} overviewKey={overviewKey} center={view.center} zoom={view.zoom} homePoints={homePoints} fitOnMount={Boolean(homePoints)} data-id="element-5419" data-mp="11zje2kfozb4x" />

          {/* District fills */}
          {shownDistricts.map(d => d.polygon ? <Polygon key={`fill-${d.id}-${isOn(d.id) ? 'on' : 'off'}`} positions={d.polygon} eventHandlers={{
          click: () => onDistrictClick(d.id)
        }} pathOptions={{
          stroke: false,
          fillColor: tone(d.id).fill,
          fillOpacity: isOn(d.id) ? 0.12 : 0.2,
          className: 'cursor-pointer'
        }} data-id="element-5420" data-mp="1kopqjppw76ia" /> : d.sectors.map(s => <Polygon key={`fill-${d.id}-${s.id}-${isOn(d.id) ? 'on' : 'off'}`} positions={s.polygon} eventHandlers={{
          click: () => onDistrictClick(d.id)
        }} pathOptions={{
          stroke: false,
          fillColor: tone(d.id).fill,
          fillOpacity: isOn(d.id) ? 0.12 : 0.2,
          className: 'cursor-pointer'
        }} data-id="element-5421" data-mp="248ew42gzumfs" />))}

          {/* Sectors overlay */}
          {overlaySectors.map(s => {
          const active = activeSectorIds.includes(s.id);
          const st = onSectorClick && watchSectorIds.includes(s.id) ? AMBER : GREEN;
          return <Polygon key={`sector-${s.id}-${active ? 'on' : 'off'}-${onSectorClick ? 'c' : 's'}`} positions={s.polygon} interactive={Boolean(onSectorClick)} eventHandlers={onSectorClick ? {
            click: () => onSectorClick(s.id)
          } : undefined} pathOptions={{
            color: st.sector,
            weight: active && onSectorClick ? 2.5 : 1,
            dashArray: active && onSectorClick ? undefined : '4 4',
            fillColor: st.fill,
            fillOpacity: active ? 0.4 : st === AMBER ? 0.25 : 0.05,
            className: onSectorClick ? 'cursor-pointer' : ''
          }} data-id="element-5422" data-mp="xfaapc8zjajg">
                {onSectorClick && <Tooltip direction="center" permanent opacity={0.9} data-id="element-5423" data-mp="3r669k8bjqpa5">
                    <span className="font-semibold" data-id="element-5424" data-mp="3r7hck5x63t07">{sectorLetter(s)}</span>
                  </Tooltip>}
              </Polygon>;
        })}

          {/* District borders */}
          {shownDistricts.map(d => <Polyline key={`outline-${d.id}-${isOn(d.id) ? 'on' : 'off'}`} positions={districtOutline(d)} interactive={false} pathOptions={{
          color: isOn(d.id) ? tone(d.id).strong : tone(d.id).line,
          weight: isOn(d.id) ? 3 : 1.5
        }} data-id="element-5425" data-mp="26nlxaj9pmbyj" />)}

          {/* Number badges */}
          {shownDistricts.map(d => <Marker key={`badge-${d.id}-${isOn(d.id) ? 'on' : 'off'}`} position={districtCenter(d)} icon={badgeIcon(d.badge, isOn(d.id))} eventHandlers={{
          click: () => onDistrictClick(d.id)
        }} keyboard={false} data-id="element-5426" data-mp="2pdboyxi6ehm8" />)}

          <MapResize trigger={expanded} points={focusPoints} center={view.center} zoom={view.zoom} data-id="element-5427" data-mp="1019nn5vopxb6" />

          {/* Request pins */}
          {showDetails && pins.filter(p => slaFilter === null || p.state === slaFilter).map(p => <Marker key={`pin-${p.id}`} position={p.position} icon={pinIcon(p.icon, p.state, p.id === selectedPinId)} zIndexOffset={p.id === selectedPinId ? 2000 : 1000} eventHandlers={{
          click: () => onPinClick(p.id)
        }} data-id="element-5428" data-mp="3gm55zxv19ihq" />)}
        </MapContainer>
      </div>
    </div>;
}